import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloudDownload from '@mui/icons-material/CloudDownload';

import { selectUser } from 'entities/user/selectors';
import { CurrencyCode } from 'utils/currencyFormat';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { lightGray, white } from 'utils/color-styles';
import { selectDonationsForPayrollRun } from 'entities/donation/selectors';
import downloadReport from 'utils/downloadReport';
import { type PayrollRunsListQuery } from 'generated/graphql';
import { stripTime } from 'utils/stripTime';
import { PayrollScheduleHeader } from 'containers/PayrollGenericPage/Schedule/PayrollScheduleHeader';
import { PayrollRunInfo } from 'containers/PayrollGenericPage/Schedule/PayrollRunInfo';
import type { State } from 'reducers';
import {
  DonationsTable,
  type GenericDonation,
} from 'containers/PayrollGenericPage/Schedule/DonationsTable';
import { executionErrorMessages } from 'entities/donation/executionErrorMessages';
import type Donation from 'entities/donation/model';

import { selectDonationsForPayrollFetchStatus } from '../selectors';
import { DonationsFetchStatus, fetchDonationsForPayrollAction } from '../types';
import reducer from '../reducer';
import epics from '../epics';
import { PayrollNote } from '../PayrollNote';
import { DonationPayrollStatus } from '../DonationPayrollStatus';

export type PayrollRun = PayrollRunsListQuery['payrollRuns'][number];

interface PayrollScheduleInterface {
  payrollRun: PayrollRun;
  setSelectedDonation: (donationId: string) => void;
  rescheduleDonationSuccess: boolean;
  setSelectedPayrollRun: (selectedPayrollRun: string | null) => void;
  selectedPayrollRun: string | null;
}

type DonationItem = GenericDonation &
  Pick<Donation, 'executionFailure' | 'payrollStatus' | 'rescheduledTo'>;

export const PayrollSchedule = ({
  payrollRun,
  setSelectedDonation,
  rescheduleDonationSuccess,
  selectedPayrollRun,
  setSelectedPayrollRun,
}: PayrollScheduleInterface): JSX.Element => {
  useInjectReducer({ key: 'payrollApiPage', reducer });
  useInjectEpics({ key: 'payrollApiPage', epics });
  const dispatch = useDispatch();

  const isUpcoming = payrollRun.runState === 'DRAFT' || payrollRun.runState === 'IN_PROGRESS';

  const user = useSelector(selectUser);
  const donationsImmutable = useSelector((state: State) =>
    selectDonationsForPayrollRun(state, payrollRun.id)
  );
  const donationsArray: DonationItem[] = donationsImmutable.toArray().map((donation) => {
    const nonprofits = donation.nonprofits?.toArray();
    return {
      ...donation.toJS(),
      Nonprofits: nonprofits.map((nonprofit) => nonprofit.toJS()),
      nonprofits: nonprofits?.map((nonprofit) => nonprofit.id),
      User: donation.user.toJS(),
      donationSchedule: donation.donationSchedule?.id,
    };
  });

  const donationsFetchStatus = useSelector((state: State) =>
    selectDonationsForPayrollFetchStatus(state, payrollRun.id)
  );
  const donationsLoading = donationsFetchStatus === DonationsFetchStatus.LOADING;
  const donationsLoaded = donationsFetchStatus === DonationsFetchStatus.LOADED;

  const isSelectedPayrollRun = selectedPayrollRun === payrollRun.id;

  useEffect(() => {
    if (isSelectedPayrollRun && !donationsLoaded && !donationsLoading) {
      dispatch(fetchDonationsForPayrollAction(payrollRun.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayrollRun, payrollRun]);

  const [summaryDownloading, setSummaryDownloading] = useState(false);

  const balance =
    payrollRun?.stats?.deductionActual && payrollRun?.stats?.deductionIntent
      ? payrollRun.stats.deductionActual - payrollRun.stats.deductionIntent
      : undefined;

  const onTogglePayrollRun = () => {
    setSelectedPayrollRun(payrollRun.id === selectedPayrollRun ? null : payrollRun.id);
  };

  return (
    <div>
      <PayrollScheduleHeader
        payrollRun={payrollRun}
        onTogglePayrollRun={onTogglePayrollRun}
        isUpcoming={isUpcoming}
        isSelected={isSelectedPayrollRun}
        extra={
          <IconButton
            style={{
              border: `1px solid ${lightGray}`,
              width: 40,
              height: 40,
              backgroundColor: white,
              marginLeft: 'auto',
            }}
            disabled={summaryDownloading}
            onClick={() => {
              setSummaryDownloading(true);
              downloadReport(
                'donations',
                {
                  payrollRun: payrollRun.id,
                  sort: 'date',
                  order: 'asc',
                  export: true,
                },
                `payroll-${payrollRun.id}`
              ).then(() => {
                setSummaryDownloading(false);
              });
            }}
          >
            <CloudDownload />
          </IconButton>
        }
      />
      <PayrollRunInfo
        isUpcoming={isUpcoming}
        deductionIntent={
          payrollRun.stats?.deductionIntent
            ? {
                amount: payrollRun.stats.deductionIntent,
                currency: user.displayCurrency as CurrencyCode,
              }
            : undefined
        }
        extra={
          <PayrollNote
            balance={balance}
            isUpcoming={isUpcoming}
            nextPayrollDate={stripTime(payrollRun.payDate || payrollRun.endDate)}
            userDisplayCurrency={user.displayCurrency}
          />
        }
      />
      <Collapse in={payrollRun.id === selectedPayrollRun} timeout="auto" unmountOnExit>
        <DonationsTable<DonationItem>
          loading={donationsLoading}
          donations={donationsArray}
          renderStatus={({ donation }) => (
            <>
              <DonationPayrollStatus
                status={donation.payrollStatus}
                id={donation.id}
                setSelectedDonation={setSelectedDonation}
                executionErrorMessage={executionErrorMessages[donation.executionFailure?.errorCode]}
                canRescheduleDonation={
                  !donation.rescheduledTo &&
                  donation.executionFailure?.errorCode === 'Deduction_missing'
                }
                rescheduledTo={donation.rescheduledTo}
              />
              <span>{rescheduleDonationSuccess && !donation.rescheduledTo}</span>
            </>
          )}
        />
      </Collapse>
    </div>
  );
};

export default PayrollSchedule;
